import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Loader2, Send, Copy, Trash2, Calendar, Clock } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

const statusBadge = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  scheduled: "bg-amber-50 text-amber-700 border-amber-200",
  sending: "bg-sky-50 text-sky-700 border-sky-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};

export default function Broadcasts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/broadcasts");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    await api.post(`/broadcasts/${id}/duplicate`);
    toast.success("Duplicated");
    load();
  };
  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete broadcast?",
      description: "The broadcast and all its delivery logs will be permanently removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/broadcasts/${id}`);
    toast.success("Deleted");
    load();
  };
  const send = async (id) => {
    const ok = await confirm({
      title: "Send broadcast now?",
      description: "Recipients will receive this message immediately.",
      confirmText: "Send now",
    });
    if (!ok) return;
    try {
      await api.post(`/broadcasts/${id}/send`);
      toast.success("Broadcast sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="broadcasts-page">
      <PageHeader
        title="Broadcasts"
        description="Send WhatsApp messages to segments of your audience"
        actions={
          <Button onClick={() => navigate("/broadcasts/new")} className="bg-brand hover:bg-brand-dark" data-testid="broadcast-new-button">
            <Plus className="w-4 h-4 mr-1.5" />New broadcast
          </Button>
        }
      />

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      ) : items.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-12 text-center">
            <Send className="w-10 h-10 text-slate-300 mx-auto" />
            <div className="mt-3 font-heading text-lg font-semibold text-slate-700">No broadcasts yet</div>
            <p className="text-sm text-slate-500 mt-1">Create your first WhatsApp broadcast</p>
            <Button onClick={() => navigate("/broadcasts/new")} className="mt-4 bg-brand hover:bg-brand-dark">
              <Plus className="w-4 h-4 mr-1.5" />New broadcast
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <Card key={b.id} data-testid={`broadcast-row-${b.id}`} className="border-slate-200 shadow-none">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/broadcasts/${b.id}`} className="font-heading font-semibold text-slate-900 hover:text-brand-dark">
                        {b.name}
                      </Link>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge[b.status] || statusBadge.draft}`}>
                        {b.status}
                      </span>
                      {b.scheduled_at && b.status === "scheduled" && (
                        <span className="inline-flex items-center text-xs text-amber-700 gap-1">
                          <Clock className="w-3 h-3" /> {new Date(b.scheduled_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1 line-clamp-1">{b.message_body}</div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span><span className="font-semibold text-slate-700">{b.stats?.total ?? 0}</span> audience</span>
                      <span><span className="font-semibold text-slate-700">{b.stats?.sent ?? 0}</span> sent</span>
                      <span><span className="font-semibold text-emerald-700">{b.stats?.delivered ?? 0}</span> delivered</span>
                      <span><span className="font-semibold text-brand-dark">{b.stats?.read ?? 0}</span> read</span>
                      <span><span className="font-semibold text-red-700">{b.stats?.failed ?? 0}</span> failed</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {(b.status === "draft" || b.status === "scheduled") && (
                      <Button size="sm" onClick={() => send(b.id)} className="bg-brand hover:bg-brand-dark" data-testid={`broadcast-send-${b.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" />Send now
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => duplicate(b.id)} data-testid={`broadcast-duplicate-${b.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(b.id)} className="text-red-600" data-testid={`broadcast-delete-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
